import { Box, GlobalStyles, useTheme } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { Group, Panel, Separator, type PanelSize } from 'react-resizable-panels';
import { useParams } from 'react-router-dom';

import {
  addAvailabilityMatcher,
  closeResearchAssistant,
  openResearchAssistant,
  updateAvailabilityMatchers,
  useIsResearchAssistantEnabled,
  useIsResearchAssistantOpen,
  useResearchAssistantStore,
} from '@verantos-dev/app-state-research-assistant';
import { useGetProjectQuery } from '@verantos-dev/data-access-projects';

import { PanelCollapseStrip } from './PanelCollapseStrip';
import { MaybeActiveChatProvider, ProjectChatPanel } from './ProjectChatPanel';
import { ProjectContentOutlet } from './ProjectContentOutlet';
import { ProjectPageHeaderContainer } from './ProjectPageHeaderContainer';
import {
  deriveProjectPanelLayout,
  projectPanelLayoutLevers,
  type ProjectPanelLayout,
} from './projectPanelLayoutModel';
import { ProjectPanelLayoutProvider } from '../context/ProjectPanelLayoutContext';
import { useProjectPageAnalyticalDatasetSync } from '../hooks/useProjectPageAnalyticalDatasetSync';
import { useProjectPageAnalyticalVariableSync } from '../hooks/useProjectPageAnalyticalVariableSync';
import { useProjectPageCohortSync } from '../hooks/useProjectPageCohortSync';
import { useProjectPanelLayout } from '../hooks/useProjectPanelLayout';
import { useEffectiveChatId } from '../hooks/useStickyActiveChat';

// Phase 4: while ProjectPage is mounted, RA is enabled across the entire
// agent-first project tree (root + nested artifact routes). This wide
// matcher is added at runtime and removed on unmount so legacy users
// (gen_3 OFF) keep their narrower matcher set in initial state.
//
// `chatAvailableProjectRoute` keeps RA off the analytical-dataset surfaces
// (`?kind=analytical_dataset` list filter and the `/analytical-datasets`
// subtree), which have no chat surface. The cohort builder Results tab is
// now included — the characteristics charts are container-responsive so they
// reflow rather than overflow when the chat sidebar opens beside them.
// TODO: cleanup with research_assistant_gen_3 ff
const PROJECT_TREE_AVAILABILITY_MATCHER = {
  path: '^/projects/[^/]+(/.*)?$',
  isRegexPath: true,
  conditions: {
    customCondition: 'chatAvailableProjectRoute',
  },
} as const;

const CHAT_PANEL_ID = 'chat';
const CONTENT_PANEL_ID = 'content';

// Pixel min widths protect both panels from being squashed past usability.
// The content panel's floor accommodates the project-artifacts datagrid's
// column min-widths (~844px sum) plus the surrounding gutter padding so
// the table doesn't horizontally scroll within the panel.
//
// The chat floor equals the first-load default (`DEFAULT_CHAT_PIXELS`,
// 428px): the chat is meant to hold its width, so the user can drag it
// *wider* but not narrower than its default. Combined with the chat
// panel's `preserve-pixel-size` resize behavior below, this means extra
// width from a growing window flows to the content panel and the chat
// stays put unless the user drags it.
const CHAT_PANEL_MIN_PX = '428px';
const CONTENT_PANEL_MIN_PX = '840px';

// Mount transition: each render branch fades + slides into place when it
// becomes active so the chat / content show & hide doesn't snap.
const TRANSITION_MS = 340;
const TRANSITION_EASING = 'cubic-bezier(0.2, 0.8, 0.2, 1)';
const slideInFromLeft = {
  '@keyframes projectPageSlideInLeft': {
    from: { opacity: 0, transform: 'translateX(-24px)' },
    to: { opacity: 1, transform: 'translateX(0)' },
  },
  animation: `projectPageSlideInLeft ${TRANSITION_MS}ms ${TRANSITION_EASING}`,
};
const slideInFromRight = {
  '@keyframes projectPageSlideInRight': {
    from: { opacity: 0, transform: 'translateX(24px)' },
    to: { opacity: 1, transform: 'translateX(0)' },
  },
  animation: `projectPageSlideInRight ${TRANSITION_MS}ms ${TRANSITION_EASING}`,
};
const fadeIn = {
  '@keyframes projectPageFadeIn': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  animation: `projectPageFadeIn ${TRANSITION_MS}ms ${TRANSITION_EASING}`,
};

/**
 * Agent-first project home. Routes:
 *
 *   /projects/:projectId                       — chat empty-state + artifact list
 *   /projects/:projectId/chats/:chatId         — active chat thread + artifact list
 *   /projects/:projectId/<detail-route>        — chat + nested detail (cohort, AV, CG)
 *
 * One layout, three states:
 * - **Left (GRA chat panel)** — `EpiChatPanelLayout` when `chatId` is set,
 *   {@link ChatPanelEmptyState} (chats list + new-chat composer) when
 *   not. Session management is a panel-internal affordance; the chat
 *   panel is always present.
 * - **Right (content outlet)** — `ProjectContentOutlet` renders the
 *   matched route element: `ProjectArtifactsListPage` on the index, or
 *   a detail page (cohort / AV / CG) when navigated deeper.
 *
 * TODO: cleanup with research_assistant_gen_3 ff
 */
export const ProjectPage = () => {
  const theme = useTheme();
  const { projectId } = useParams<{ projectId: string }>();
  const safeProjectId = projectId ?? '';
  // Chat is a query-param sidecar (see ProjectChatPanel chatId prop doc).
  // ``useEffectiveChatId`` resolves URL → sticky-storage synchronously
  // so MaybeActiveChatProvider sees a stable chatId across in-project
  // navigation gaps (e.g. cohort-builder tab nav that drops ``?chat=``).
  const chatId = useEffectiveChatId(safeProjectId);

  // Invalidate artifact manifest + activity feed whenever a cohort save/process/discard
  // completes, regardless of which sub-route is currently active.
  useProjectPageCohortSync(safeProjectId);

  const { data: projectData, isLoading: isProjectLoading } = useGetProjectQuery(safeProjectId, {
    skip: !safeProjectId,
  });
  const projectName = projectData?.data.projectName;
  const projectDatasetReleaseName = projectData?.data.datasetReleaseName ?? null;
  const projectDatasetReleaseId = projectData?.data.datasetReleaseId ?? null;
  // Gates the dataset switcher: only the owning org may repoint a project.
  const projectOrganizationId = projectData?.data.organizationId ?? null;

  // Invalidate the artifact manifest + activity feed whenever an analytical-dataset
  // save/process completes, regardless of which sub-route is currently active.
  useProjectPageAnalyticalDatasetSync(safeProjectId);

  // Invalidate the artifact manifest + activity feed whenever an analytical-variable
  // write (e.g. rename, OR-6090) completes, regardless of which sub-route is active.
  useProjectPageAnalyticalVariableSync(safeProjectId);

  const isChatOpen = useIsResearchAssistantOpen();
  // Route-based RA availability (matchers in store, evaluated by
  // ``useResearchAssistantAvailability`` mounted globally by
  // ``GlobalResearchAssistant``). When the matcher rejects the current
  // route — notably the cohort-builder Results tab — the chat panel must
  // not render at all, even if the user previously opened it on a
  // sibling tab. The user-toggled open state stays untouched so chat
  // returns to its prior state when the matcher accepts the route again.
  // TODO: cleanup with research_assistant_gen_3 ff
  const isRAEnabled = useIsResearchAssistantEnabled();
  const showChat = isChatOpen && isRAEnabled;

  const { initialChatSize, initialSegment, persistChatPixels, persistLayout } =
    useProjectPanelLayout(safeProjectId);

  const [contentCollapsed, setContentCollapsed] = useState(() => initialSegment === 'ra-only');

  // Restore the persisted 3-way layout per project (OR-6123 F4). `showChat`
  // is global (RA store) and `contentCollapsed` is local, so the restore
  // must apply BOTH levers and re-run on `projectId` change — otherwise the
  // global RA-open flag carried over from a sibling project would defeat a
  // saved `artifacts-only`, and a saved choice would never re-apply when
  // navigating between projects. With no saved preference we default to the
  // split layout (RA open, content expanded).
  // TODO: cleanup with research_assistant_gen_3 ff
  useEffect(() => {
    const levers = projectPanelLayoutLevers(initialSegment ?? 'split');
    if (levers.showChat) {
      openResearchAssistant();
    } else {
      closeResearchAssistant();
    }
    setContentCollapsed(levers.contentCollapsed);
    // `initialSegment` is a one-time-per-project restore seed (the hook
    // recomputes it on `projectId` change); keying the effect on it applies
    // the seed exactly once per project load.
  }, [safeProjectId, initialSegment]);

  useEffect(() => {
    addAvailabilityMatcher(PROJECT_TREE_AVAILABILITY_MATCHER);
    return () => {
      const current = useResearchAssistantStore.getState().availabilityMatchers;
      const next = current.filter((m) => m.path !== PROJECT_TREE_AVAILABILITY_MATCHER.path);
      updateAvailabilityMatchers(next);
    };
  }, []);

  const handleChatResize = useCallback(
    (size: PanelSize) => {
      persistChatPixels(Math.round(size.inPixels));
    },
    [persistChatPixels]
  );

  // 3-way panel layout (OR-6123 F3). The active segment is derived from the
  // two existing levers (RA visibility + content collapse) so the toggle can
  // never desync from what's actually rendered; applying a layout maps back
  // to those levers (open/close the RA store + set local collapse). The
  // collapse-to-strip visuals + persistence are deferred to F4.
  // TODO: cleanup with research_assistant_gen_3 ff
  const panelLayout = deriveProjectPanelLayout({ showChat, contentCollapsed });

  const handleChangePanelLayout = useCallback(
    (layout: ProjectPanelLayout) => {
      const levers = projectPanelLayoutLevers(layout);
      if (levers.showChat) {
        openResearchAssistant();
      } else {
        closeResearchAssistant();
      }
      setContentCollapsed(levers.contentCollapsed);
      // Persist on explicit user intent only (OR-6123 F4). The derived
      // `panelLayout` flips to `artifacts-only` whenever the RA-availability
      // matcher rejects a route (e.g. the cohort-builder Results tab), so
      // persisting the derived value would silently clobber the real
      // preference — we persist the chosen layout here instead.
      persistLayout(layout);
    },
    [persistLayout]
  );

  // Right pane is always the route outlet — `ProjectArtifactsListPage` on
  // the project index, or a detail page (cohort / AV / CG) on a deeper
  // route. Multi-chat session management is panel-internal: the chat
  // panel decides between the active-thread layout and the empty-state
  // (chats list + new-chat composer) based on `chatId`.
  const rightPaneOutlet = <ProjectContentOutlet />;
  const showSplit = showChat && !contentCollapsed;

  // First-load model (OR-6123 F4): no full-page skeleton. The header renders
  // immediately (its title shows an inline loading skeleton via
  // `isProjectNameLoading`), and the chat panel + content outlet each render
  // their own in-place skeleton while their data loads. Every page request is
  // keyed on ids available at mount, so they all fire in parallel on first
  // render rather than waterfalling behind the project query.
  // TODO: cleanup with research_assistant_gen_3 ff

  return (
    <Box
      data-testid="project-page"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        minHeight: 0,
        backgroundColor: theme.palette.background.default,
      }}
    >
      {/* Widen the resize-handle hit area without thickening the visible
          divider. The Separator itself stays at 1px (matching the legacy
          gen_3-OFF look); ::before extends a transparent 8px grab zone
          that carries the col-resize cursor. */}
      <GlobalStyles
        styles={{
          '.project-page-resize-handle::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: -4,
            right: -4,
            cursor: 'col-resize',
          },
        }}
      />
      {/* The header sits inside MaybeActiveChatProvider so it can read the
          chat's run state for the dataset-switch control. The provider renders
          no DOM of its own, so wrapping the header changes nothing visually. */}
      <MaybeActiveChatProvider projectId={safeProjectId} chatId={chatId}>
        <ProjectPageHeaderContainer
          projectId={safeProjectId}
          projectName={projectName}
          isProjectNameLoading={isProjectLoading}
          datasetReleaseId={projectDatasetReleaseId}
          datasetReleaseName={projectDatasetReleaseName}
          projectOrganizationId={projectOrganizationId}
          panelLayout={panelLayout}
          onChangePanelLayout={handleChangePanelLayout}
        />
        {/* Step 6: when a chat is open, both panes share one
          ``useEpiChatPage`` mount via {@link ActiveChatProvider} (the
          right-pane draft route reads the same artifact stream the chat
          panel does). Without an open chat the provider doesn't mount —
          there's no chat stream to share.
          TODO: cleanup with research_assistant_gen_3 ff */}
        <ProjectPanelLayoutProvider layout={panelLayout}>
          <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex' }}>
            {showSplit ? (
              <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', ...fadeIn }}>
                <Group
                  id={`project-split-${safeProjectId || 'unknown'}`}
                  orientation="horizontal"
                  style={{ flex: 1, minWidth: 0, minHeight: 0, height: '100%' }}
                  data-testid="project-page-split"
                >
                  <Panel
                    id={CHAT_PANEL_ID}
                    defaultSize={initialChatSize}
                    minSize={CHAT_PANEL_MIN_PX}
                    // Hold the chat at its pixel width when the Group (window)
                    // resizes; the content panel (default `preserve-relative-size`)
                    // absorbs the change instead. The user can still drag the
                    // separator to set a new chat width.
                    groupResizeBehavior="preserve-pixel-size"
                    onResize={handleChatResize}
                    style={{ minWidth: 0, minHeight: 0, display: 'flex' }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        width: '100%',
                        borderRight: `1px solid ${theme.palette.divider}`,
                        display: 'flex',
                        minHeight: 0,
                        minWidth: 0,
                      }}
                    >
                      <ProjectChatPanel projectId={safeProjectId} chatId={chatId} />
                    </Box>
                  </Panel>
                  <Separator
                    id="project-page-resize-handle"
                    aria-label="Resize chat panel"
                    className="project-page-resize-handle"
                    // 1px visual stripe matches the legacy thin divider on
                    // the cohort-builder split with gen_3 OFF. A wider
                    // transparent hit area sits on top via ::before so the
                    // col-resize cursor + drag remain comfortable without
                    // making the divider look chunky.
                    style={{
                      width: 1,
                      backgroundColor: theme.palette.divider,
                      position: 'relative',
                      cursor: 'col-resize',
                    }}
                  />
                  <Panel
                    id={CONTENT_PANEL_ID}
                    // Chat's `defaultSize` is pixel-based, so there's no
                    // percent to sum to 100 — omit and let this fill the rest.
                    minSize={CONTENT_PANEL_MIN_PX}
                    style={{ minWidth: 0, minHeight: 0, display: 'flex' }}
                  >
                    {rightPaneOutlet}
                  </Panel>
                </Group>
              </Box>
            ) : showChat ? (
              // RA-only: chat takes the row; the artifacts pane collapses to a
              // thin right strip whose chevron expands it back toward split
              // (OR-6123 F4 — COLLAPSED ARTIFACTS 63:1850).
              <Box
                data-testid="project-page-chat-only"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  minHeight: 0,
                  display: 'flex',
                  ...slideInFromLeft,
                }}
              >
                <Box
                  data-testid="project-page-chat-only-pane"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    minHeight: 0,
                    display: 'flex',
                  }}
                >
                  <ProjectChatPanel projectId={safeProjectId} chatId={chatId} />
                </Box>
                <PanelCollapseStrip
                  side="right"
                  visibleLabel="Workspace"
                  data-testid="project-page-artifacts-strip"
                />
              </Box>
            ) : (
              // Artifacts-only: the outlet takes the row; the RA pane collapses
              // to a thin left strip whose chevron expands it back toward split
              // (OR-6123 F4 — COLLAPSED RA 65:10103). The strip only stands in
              // for a *user-collapsed* RA, not an availability-disabled one, so
              // it's gated on `showChat`-capability rather than the derived
              // segment alone.
              <Box
                data-testid="project-page-content-only"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  minHeight: 0,
                  display: 'flex',
                  ...slideInFromRight,
                }}
              >
                {isRAEnabled ? (
                  <PanelCollapseStrip
                    side="left"
                    visibleLabel="Research Assistant"
                    data-testid="project-page-ra-strip"
                  />
                ) : null}
                <Box
                  data-testid="project-page-content-only-pane"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    minHeight: 0,
                  }}
                >
                  {rightPaneOutlet}
                </Box>
              </Box>
            )}
          </Box>
        </ProjectPanelLayoutProvider>
      </MaybeActiveChatProvider>
    </Box>
  );
};

export default ProjectPage;
